import { useState } from 'react';
import { Button, Dialog, Input } from '../../components/ui';
import { ContentRecommendationModalDTO } from './content-recommendations';
import {
  useCreateContentRecommendation,
  useDeleteContentRecommendation,
  useUpdateContentRecommendation,
} from '../../hooks/useContentRecommendations';
import { useVerticals } from '../../hooks/useClients';
import { toast } from 'react-toastify';

export interface ContentRecommendationsModalProps {
  recommendation: ContentRecommendationModalDTO;
  open: boolean;
  onClose: () => void;
}

export const ContentRecommendationsModal = ({ open, onClose, recommendation }: ContentRecommendationsModalProps) => {
  const createMutation = useCreateContentRecommendation();
  const updateMutation = useUpdateContentRecommendation();
  const deleteMutation = useDeleteContentRecommendation();
  const { data: verticals } = useVerticals();
  const [questions, setQuestions] = useState<string[]>(() =>
    recommendation?.questions?.length ? [...recommendation.questions] : [''],
  );

  if (!recommendation) return null;

  const updateQuestion = (index: number, value: string) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? value : q)));
  };

  const addQuestion = () => {
    setQuestions((prev) => [...prev, '']);
  };

  const removeQuestion = (index: number) => {
    setQuestions((prev) => {
      if (prev.length <= 1) return [''];
      return prev.filter((_, i) => i !== index);
    });
  };

  const moveQuestion = (index: number, direction: -1 | 1) => {
    setQuestions((prev) => {
      const next = index + direction;
      if (next < 0 || next >= prev.length) return prev;
      const copy = [...prev];
      const tmp = copy[index];
      copy[index] = copy[next];
      copy[next] = tmp;
      return copy;
    });
  };

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const title = String(formData.get('title') || '').trim();
    const description = String(formData.get('description') || '').trim();
    const verticalRaw = String(formData.get('vertical_id') || '');
    const vertical_id = verticalRaw ? Number(verticalRaw) : null;
    const cleanedQuestions = questions.map((q) => q.trim()).filter(Boolean);

    if (!title || !description) {
      toast.error('Title and description are required');
      return;
    }

    const payload = { title, description, questions: cleanedQuestions, vertical_id };

    if (!recommendation.isNew && recommendation.id) {
      updateMutation.mutate(
        { id: recommendation.id, payload },
        {
          onSuccess: () => {
            toast.success('Recommendation updated');
            onClose();
          },
          onError: (err: any) => {
            toast.error(err?.response?.data?.description || err?.response?.data?.error || 'Update failed');
          },
        },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toast.success('Recommendation created');
          onClose();
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.description || err?.response?.data?.error || 'Create failed');
        },
      });
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const handleDelete = () => {
    if (!recommendation.id) return;
    const confirmed = window.confirm(
      'Delete this content recommendation? Story draft sessions that selected it will keep their progress, but the topic will no longer be linked.',
    );
    if (!confirmed) return;
    deleteMutation.mutate(recommendation.id, {
      onSuccess: () => {
        toast.success('Recommendation deleted');
        onClose();
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.description || err?.response?.data?.error || 'Delete failed');
      },
    });
  };

  return (
    <Dialog key={recommendation.id ?? 'new'} className="w-2/3 p-4" open={open} onClose={onClose}>
      <h1 className="pb-4 text-2xl">{recommendation.isNew ? 'Add' : 'Edit'} Content Recommendation</h1>
      <form onSubmit={submit} className="mx-10 flex flex-col justify-center gap-x-4 gap-y-4">
        <Input
          key={`title-${recommendation.id ?? 'new'}`}
          onIconClick={() => {}}
          id="title"
          name="title"
          defaultValue={recommendation.title || ''}
          placeholder="Short topic label"
          label="Title"
        />
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Description</span>
          <textarea
            name="description"
            defaultValue={recommendation.description || ''}
            placeholder="Guidance text shown to the draft coach"
            className="min-h-[120px] rounded border border-gray-300 p-2"
            required
          />
        </label>
        <div className="flex flex-col gap-2 text-sm">
          <span className="font-medium">Probing questions</span>
          <p className="text-gray-500">The draft coach asks these one at a time after the peer picks this topic.</p>
          {questions.map((question, index) => (
            <div key={index} className="flex items-start gap-2">
              <span className="mt-2 w-6 shrink-0 text-gray-400">{index + 1}.</span>
              <textarea
                value={question}
                onChange={(e) => updateQuestion(index, e.target.value)}
                placeholder="What was happening in your life when this started?"
                className="min-h-[64px] flex-1 rounded border border-gray-300 p-2"
              />
              <div className="flex flex-col gap-1 pt-1">
                <button
                  type="button"
                  className="rounded border px-2 py-0.5 text-xs disabled:opacity-40"
                  disabled={index === 0}
                  onClick={() => moveQuestion(index, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="rounded border px-2 py-0.5 text-xs disabled:opacity-40"
                  disabled={index === questions.length - 1}
                  onClick={() => moveQuestion(index, 1)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="rounded border px-2 py-0.5 text-xs text-red-600"
                  onClick={() => removeQuestion(index)}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
          <button type="button" className="self-start rounded border px-3 py-1.5 text-sm" onClick={addQuestion}>
            Add question
          </button>
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Vertical</span>
          <select
            name="vertical_id"
            defaultValue={recommendation.vertical_id ?? ''}
            className="rounded border border-gray-300 p-2"
          >
            <option value="">All verticals</option>
            {verticals?.map((vertical) => (
              <option key={vertical.id} value={vertical.id}>
                {vertical.name}
              </option>
            ))}
          </select>
        </label>
        <div className="flex justify-between pb-4 pt-10">
          {!recommendation.isNew ? (
            <button
              type="button"
              className="bg-redSalsa rounded px-4 py-2 text-white disabled:opacity-50"
              onClick={handleDelete}
              disabled={isSubmitting}
            >
              Delete
            </button>
          ) : (
            <div />
          )}
          <div className="flex gap-x-4">
            <Button onClick={onClose} variant="secondary" disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Submit'}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
};
